import type { BoardGraph } from './graph';
import type { BoardNode } from './types';
import { isTilePassthrough } from './tiles';

export interface BoardDirection {
  dx: number;
  dz: number;
}

export interface WireDirection {
  dx: number;
  /** Board-space z component. Kept as `dy` for wire compatibility. */
  dy: number;
}

export interface ForwardRayStep {
  nodeId: string;
  pathIndex: number;
  landableIndex: number | null;
}

export interface ForwardRayResult {
  path: string[];
  landablePath: string[];
  steps: ForwardRayStep[];
  finalNodeId: string;
}

export interface WalkPathResult {
  /** Full shortest path, including start and end nodes. */
  path: string[];
  /** Movement distance, counting non-pathing nodes after the start. */
  distance: number;
}

export interface WalkForwardRayOptions {
  maxLandableTiles: number;
  stopAtNodeId?: string | null;
}

export interface FindWalkPathOptions {
  maxLandableTiles?: number;
}

export interface WalkForwardGraphPathOptions {
  maxLandableTiles: number;
  stopAtNodeId?: string | null;
}

const FORWARD_EPSILON = 1e-6;

export function directionToBoardVector(direction: WireDirection): BoardDirection {
  return {
    dx: Number.isFinite(direction.dx) ? direction.dx : 0,
    dz: Number.isFinite(direction.dy) ? direction.dy : 0,
  };
}

export function directionBetweenNodes(
  graph: BoardGraph,
  fromNodeId: string,
  toNodeId: string,
): WireDirection | undefined {
  const fromNode = graph.getNode(fromNodeId);
  const toNode = graph.getNode(toNodeId);
  if (fromNode == null || toNode == null) return undefined;
  const dx = toNode.position.x - fromNode.position.x;
  const dy = toNode.position.z - fromNode.position.z;
  if (dx === 0 && dy === 0) return undefined;
  return { dx, dy };
}

export function walkForwardRay(
  graph: BoardGraph,
  startNodeId: string,
  direction: WireDirection,
  options: WalkForwardRayOptions,
): ForwardRayResult {
  const startNode = graph.getNode(startNodeId);
  if (startNode == null || options.maxLandableTiles <= 0) {
    return { path: [], landablePath: [], steps: [], finalNodeId: startNodeId };
  }

  const boardDirection = directionToBoardVector(direction);
  const directionMag = Math.hypot(boardDirection.dx, boardDirection.dz);
  if (directionMag <= FORWARD_EPSILON) {
    return { path: [], landablePath: [], steps: [], finalNodeId: startNodeId };
  }

  const path: string[] = [];
  const landablePath: string[] = [];
  const steps: ForwardRayStep[] = [];
  const visited = new Set<string>([startNodeId]);
  let currentNode: BoardNode = startNode;
  let currentNodeId = startNodeId;

  while (landablePath.length < options.maxLandableTiles) {
    const next = chooseForwardWalkNeighbor(
      graph,
      currentNode,
      boardDirection,
      directionMag,
      visited,
    );
    if (next == null) break;

    const isPassthrough = isTilePassthrough(next.type);
    if (!isPassthrough && landablePath.length + 1 > options.maxLandableTiles) break;

    path.push(next.id);
    visited.add(next.id);
    currentNode = next;
    currentNodeId = next.id;

    let landableIndex: number | null = null;
    if (!isPassthrough) {
      landablePath.push(next.id);
      landableIndex = landablePath.length - 1;
    }
    steps.push({ nodeId: next.id, pathIndex: path.length - 1, landableIndex });

    if (options.stopAtNodeId != null && next.id === options.stopAtNodeId) break;
  }

  while (steps.length > 0 && steps.at(-1)?.landableIndex == null) {
    steps.pop();
    path.pop();
  }

  currentNodeId = path.at(-1) ?? startNodeId;
  return { path, landablePath, steps, finalNodeId: currentNodeId };
}

export function findWalkPath(
  graph: BoardGraph,
  startNodeId: string,
  endNodeId: string,
  options: FindWalkPathOptions = {},
): WalkPathResult {
  if (graph.getNode(startNodeId) == null || graph.getNode(endNodeId) == null) {
    return { path: [], distance: -1 };
  }
  if (startNodeId === endNodeId) {
    return { path: [startNodeId], distance: 0 };
  }

  const maxLandableTiles = options.maxLandableTiles ?? Infinity;
  const queue: Array<{ nodeId: string; path: string[]; cost: number; order: number }> = [
    { nodeId: startNodeId, path: [startNodeId], cost: 0, order: 0 },
  ];
  const bestCostByNode = new Map<string, number>([[startNodeId, 0]]);
  let sequence = 1;

  while (queue.length > 0) {
    queue.sort((a, b) => a.cost - b.cost || a.order - b.order);
    const current = queue.shift()!;

    if (current.nodeId === endNodeId) {
      return { path: current.path, distance: current.cost };
    }

    for (const { node: neighbor, connectionType } of graph.getNeighbors(current.nodeId)) {
      if (connectionType !== 'walk') continue;
      const nextCost = current.cost + (isTilePassthrough(neighbor.type) ? 0 : 1);
      if (nextCost > maxLandableTiles) continue;
      const previousBest = bestCostByNode.get(neighbor.id);
      if (previousBest != null && previousBest <= nextCost) continue;
      bestCostByNode.set(neighbor.id, nextCost);
      queue.push({
        nodeId: neighbor.id,
        path: [...current.path, neighbor.id],
        cost: nextCost,
        order: sequence++,
      });
    }
  }

  return { path: [], distance: -1 };
}

export function walkForwardGraphPath(
  graph: BoardGraph,
  startNodeId: string,
  firstNodeId: string,
  options: WalkForwardGraphPathOptions,
): ForwardRayResult {
  const startNode = graph.getNode(startNodeId);
  const firstNode = graph.getNode(firstNodeId);
  if (startNode == null || firstNode == null || options.maxLandableTiles <= 0) {
    return { path: [], landablePath: [], steps: [], finalNodeId: startNodeId };
  }
  if (
    !graph
      .getNeighbors(startNodeId)
      .some((neighbor) => neighbor.connectionType === 'walk' && neighbor.node.id === firstNodeId)
  ) {
    return { path: [], landablePath: [], steps: [], finalNodeId: startNodeId };
  }

  const path: string[] = [];
  const landablePath: string[] = [];
  const steps: ForwardRayStep[] = [];
  const visited = new Set<string>([startNodeId]);
  let currentNode: BoardNode | null = firstNode;

  while (currentNode != null && landablePath.length < options.maxLandableTiles) {
    const isPassthrough = isTilePassthrough(currentNode.type);
    if (!isPassthrough && landablePath.length >= options.maxLandableTiles) break;

    path.push(currentNode.id);
    visited.add(currentNode.id);

    let landableIndex: number | null = null;
    if (!isPassthrough) {
      landablePath.push(currentNode.id);
      landableIndex = landablePath.length - 1;
    }
    steps.push({ nodeId: currentNode.id, pathIndex: path.length - 1, landableIndex });

    if (options.stopAtNodeId != null && currentNode.id === options.stopAtNodeId) break;
    if (landablePath.length >= options.maxLandableTiles) break;

    currentNode = chooseNextAuthoredWalkNeighbor(graph, currentNode, visited);
  }

  while (steps.length > 0 && steps.at(-1)?.landableIndex == null) {
    steps.pop();
    path.pop();
  }

  return {
    path,
    landablePath,
    steps,
    finalNodeId: path.at(-1) ?? startNodeId,
  };
}

export function nodesWithinGraphRange(
  graph: BoardGraph,
  startNodeId: string,
  maxLandableTiles: number,
): Set<string> {
  const startNode = graph.getNode(startNodeId);
  if (startNode == null || maxLandableTiles < 0) return new Set();

  const result = new Set<string>();
  const bestCostByNode = new Map<string, number>([[startNodeId, 0]]);
  const queue: Array<{ nodeId: string; cost: number }> = [{ nodeId: startNodeId, cost: 0 }];

  if (!isTilePassthrough(startNode.type)) {
    result.add(startNodeId);
  }

  for (let index = 0; index < queue.length; index++) {
    const current = queue[index]!;
    for (const { node: neighbor, connectionType } of graph.getNeighbors(current.nodeId)) {
      if (connectionType !== 'walk') continue;
      const nextCost = current.cost + (isTilePassthrough(neighbor.type) ? 0 : 1);
      if (nextCost > maxLandableTiles) continue;
      const previousBest = bestCostByNode.get(neighbor.id);
      if (previousBest != null && previousBest <= nextCost) continue;

      bestCostByNode.set(neighbor.id, nextCost);
      queue.push({ nodeId: neighbor.id, cost: nextCost });
      if (!isTilePassthrough(neighbor.type)) {
        result.add(neighbor.id);
      }
    }
  }

  return result;
}

function chooseForwardWalkNeighbor(
  graph: BoardGraph,
  currentNode: BoardNode,
  direction: BoardDirection,
  directionMag: number,
  visited: ReadonlySet<string>,
): BoardNode | null {
  let best: { node: BoardNode; score: number } | null = null;
  for (const { node, connectionType } of graph.getNeighbors(currentNode.id)) {
    if (connectionType !== 'walk') continue;
    if (visited.has(node.id)) continue;
    const edgeDx = node.position.x - currentNode.position.x;
    const edgeDz = node.position.z - currentNode.position.z;
    const edgeMag = Math.hypot(edgeDx, edgeDz);
    if (edgeMag <= FORWARD_EPSILON) continue;
    const score = (direction.dx * edgeDx + direction.dz * edgeDz) / (directionMag * edgeMag);
    if (score <= FORWARD_EPSILON) continue;
    if (
      best == null ||
      score > best.score + FORWARD_EPSILON ||
      (Math.abs(score - best.score) <= FORWARD_EPSILON && node.id < best.node.id)
    ) {
      best = { node, score };
    }
  }
  return best?.node ?? null;
}

function chooseNextAuthoredWalkNeighbor(
  graph: BoardGraph,
  currentNode: BoardNode,
  visited: ReadonlySet<string>,
): BoardNode | null {
  for (const { node, connectionType } of graph.getNeighbors(currentNode.id)) {
    if (connectionType !== 'walk') continue;
    if (visited.has(node.id)) continue;
    return node;
  }
  return null;
}
